'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { cn } from '@ui/lib/utils';

// Insert the global styles once into the document
if (typeof document !== 'undefined') {
  // Check if style already exists
  if (!document.getElementById('tooltip-global-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'tooltip-global-styles';
    styleElement.innerHTML = `
      .tooltip-content {
        user-select: none;
        z-index: 9999999 !important;
        pointer-events: none;
      }

      [data-radix-tooltip-content-wrapper] {
        z-index: 9999999 !important;
        pointer-events: none;
      }
    `;
    document.head.appendChild(styleElement);
  }
}

const TooltipProvider = TooltipPrimitive.Provider;

// Replace the Tooltip implementation with this simpler version
const Tooltip = ({
  children,
  preventFocusTrigger = true,
  ...props
}: TooltipPrimitive.TooltipProps & { preventFocusTrigger?: boolean }) => {
  const [allowOpen, setAllowOpen] = useState(!preventFocusTrigger);

  useEffect(() => {
    if (preventFocusTrigger) {
      const timer = setTimeout(() => setAllowOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [preventFocusTrigger]);

  // Handle the prevention logic using onOpenChange instead of controlling open state
  const handleOpenChange = (open: boolean) => {
    // If we're preventing focus trigger and not allowing open yet, prevent opening
    if (preventFocusTrigger && !allowOpen && open) {
      return; // Don't call the original onOpenChange, preventing the tooltip from opening
    }

    // Otherwise, call the original onOpenChange if it exists
    if (props.onOpenChange) {
      props.onOpenChange(open);
    }
  };

  const tooltipProps = preventFocusTrigger
    ? {
        ...props,
        onOpenChange: handleOpenChange,
      }
    : props;

  return (
    <TooltipPrimitive.Root {...tooltipProps}>{children}</TooltipPrimitive.Root>
  );
};

const TooltipTrigger = TooltipPrimitive.Trigger;

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> & {
  variant?: 'default' | 'dark';
  size?: 'default' | 'sm' | 'xs';
  indicatorColor?: 'green' | 'amber' | 'red' | 'none';
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      sideOffset = 4,
      variant = 'default',
      size = 'default',
      indicatorColor = 'none',
      children,
      ...props
    },
    ref
  ) => {
    const variantStyles = {
      default: 'bg-white text-slate-900 border-slate-200',
      dark: 'bg-slate-800 text-slate-100 border-slate-700',
    };

    const sizeStyles = {
      default: 'px-3 py-1.5 text-sm rounded-lg',
      sm: 'px-1.5 py-[2.5px] rounded',
      xs: 'px-1.5 py-0.5 text-xs rounded',
    };

    const indicatorStyles = {
      green: (
        <div className="w-1.5 h-1.5 rounded-full mr-1 inline-block bg-green-500" />
      ),
      amber: (
        <div className="w-1.5 h-1.5 rounded-full mr-1 inline-block bg-amber-600" />
      ),
      red: (
        <div className="w-1.5 h-1.5 rounded-full mr-1 inline-block bg-red-600" />
      ),
      none: null,
    };

    // Create wrapped content with indicator if needed
    const wrappedContent =
      indicatorColor !== 'none' ? (
        <div className="flex items-center">
          {indicatorStyles[indicatorColor]}
          <div>{children}</div>
        </div>
      ) : (
        children
      );

    return (
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'tooltip-content z-[9999999] overflow-hidden border shadow-md',
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {wrappedContent}
      </TooltipPrimitive.Content>
    );
  }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
